import { provideEventPlugins } from "@taiga-ui/event-plugins";
import { provideAnimations, provideNoopAnimations } from "@angular/platform-browser/animations";
import { ApplicationConfig, provideBrowserGlobalErrorListeners, provideZoneChangeDetection, importProvidersFrom, provideAppInitializer, inject, ErrorHandler } from '@angular/core';
import { provideRouter, withHashLocation, TitleStrategy } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, withInterceptors } from '@angular/common/http';
import { TuiTextfieldControllerModule, TuiInputModule } from '@taiga-ui/legacy';
import { defer } from 'rxjs';
import { tuiInputPhoneInternationalOptionsProvider as providePhoneOptions } from '@taiga-ui/kit';
import { TUI_DEFAULT_LANGUAGE } from '@taiga-ui/i18n/tokens';
import { tuiLanguageSwitcher } from '@taiga-ui/i18n/utils';

import { routes } from './app.routes';
import { authInterceptor } from './shared/interceptors/auth.interceptor';
import { apiOptimizationInterceptor } from './shared/interceptors/api-optimization.interceptor';
import { TUI_ENGLISH_LANGUAGE } from './shared/languages/english/english';
import { appLanguageLoader } from './shared/languages/language-loader';
import { PageTranslationService } from './shared/i18n/page-translation.service';
import { CustomTitleStrategy } from './shared/services/page-title.strategy';
import { offlineInterceptor } from './shared/native/offline.interceptor';
import { SyncService } from './shared/native/sync.service';
import { PlatformService } from './shared/native/platform.service';
import { AppUpdateService, GlobalAppErrorHandler } from './shared/services/app-update.service';
import { Capacitor } from '@capacitor/core';
import { tuiIconsProvider } from '@taiga-ui/core/tokens';
import { OFFLINE_LUCIDE_ICONS } from './shared/icons/offline-lucide-icons';

const nativeShell = typeof window !== 'undefined' && Capacitor.isNativePlatform();

// Enable hash-based routing when running inside Electron (file://) to prevent blank screens on deep links
const isElectron = typeof window !== 'undefined' && !!((window as any).api?.isElectron || (window as any).electron);
const routerFeatures = isElectron ? [withHashLocation()] : [];

export const appConfig: ApplicationConfig = {
  providers: [
    nativeShell ? provideNoopAnimations() : provideAnimations(),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, ...routerFeatures),
    { provide: TitleStrategy, useClass: CustomTitleStrategy },
    { provide: ErrorHandler, useClass: GlobalAppErrorHandler },
    provideEventPlugins(),
    tuiIconsProvider(OFFLINE_LUCIDE_ICONS),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([offlineInterceptor, apiOptimizationInterceptor, authInterceptor])
    ),
    {
      provide: TUI_DEFAULT_LANGUAGE,
      useValue: TUI_ENGLISH_LANGUAGE,
    },
    tuiLanguageSwitcher(appLanguageLoader),
    provideAppInitializer(() => inject(PageTranslationService).start()),
    provideAppInitializer(() => {
      inject(PlatformService).applyNativeChrome();
      inject(AppUpdateService).start();
      const sync = inject(SyncService);
      if (!nativeShell) {
        void sync.start();
        return;
      }
      // SQLite open competes with the first screen on 2GB devices. Start it after paint.
      const startSync = () => void sync.start();
      const idle = window.requestIdleCallback;
      if (typeof idle === 'function') idle(startSync, { timeout: 4000 });
      else setTimeout(startSync, 1500);
    }),
    importProvidersFrom(TuiTextfieldControllerModule, TuiInputModule),
    providePhoneOptions({
      metadata: defer(async () =>
        import('libphonenumber-js/max/metadata').then((m) => m.default),
      ),
    }),
  ]
};

